import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const EmployeeReviews = ({ reviews, overallRating }) => {
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [showAll, setShowAll] = useState(false);

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'sales', label: 'Sales & Marketing' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' },
    { value: 'operations', label: 'Operations' }
  ];

  const roleOptions = [
    { value: 'all', label: 'All Levels' },
    { value: 'entry', label: 'Entry Level' },
    { value: 'mid', label: 'Mid Level' },
    { value: 'senior', label: 'Senior Level' },
    { value: 'management', label: 'Management' }
  ];

  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'helpful', label: 'Most Helpful' },
    { value: 'rating-high', label: 'Highest Rating' },
    { value: 'rating-low', label: 'Lowest Rating' }
  ];

  const displayedReviews = showAll ? reviews : reviews?.slice(0, 4);

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={16}
            className={star <= rating ? "text-warning fill-warning" : "text-muted-foreground"}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 lg:p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-4">Employee Reviews</h2>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 p-6 bg-muted rounded-lg">
          <div className="text-center">
            <div className="text-5xl font-bold text-foreground mb-2">{overallRating?.average}</div>
            <div className="flex items-center justify-center mb-2">
              {renderStars(Math.round(overallRating?.average))}
            </div>
            <p className="text-sm text-muted-foreground">{overallRating?.total} reviews</p>
          </div>

          <div className="flex-1 w-full space-y-2">
            {overallRating?.breakdown?.map((item) => (
              <div key={item?.stars} className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground w-12">{item?.stars} star</span>
                <div className="flex-1 h-2 bg-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-warning rounded-full transition-smooth"
                    style={{ width: `${item?.percentage}%` }}
                  />
                </div>
                <span className="text-sm text-muted-foreground w-12 text-right">{item?.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Select
          label="Department"
          options={departmentOptions}
          value={filterDepartment}
          onChange={setFilterDepartment}
        />
        <Select
          label="Role Level"
          options={roleOptions}
          value={filterRole}
          onChange={setFilterRole}
        />
        <Select
          label="Sort By"
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
        />
      </div>
      <div className="space-y-6">
        {displayedReviews?.map((review) => (
          <div key={review?.id} className="p-6 bg-muted/50 border border-border rounded-lg">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  {renderStars(review?.rating)}
                  <span className="text-sm text-muted-foreground">{review?.date}</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">{review?.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {review?.role} • {review?.department} • {review?.employmentStatus}
                </p>
              </div>
              {review?.verified && (
                <div className="flex items-center gap-1 px-3 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                  <Icon name="CheckCircle2" size={14} />
                  <span>Verified</span>
                </div>
              )}
            </div>

            <div className="space-y-4 mb-4">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="ThumbsUp" size={16} className="text-success" />
                  Pros
                </h4>
                <p className="text-muted-foreground">{review?.pros}</p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="ThumbsDown" size={16} className="text-error" />
                  Cons
                </h4>
                <p className="text-muted-foreground">{review?.cons}</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <button className="flex items-center gap-2 hover:text-foreground transition-smooth">
                  <Icon name="ThumbsUp" size={16} />
                  <span>Helpful ({review?.helpful})</span>
                </button>
                <button className="flex items-center gap-2 hover:text-foreground transition-smooth">
                  <Icon name="Flag" size={16} />
                  <span>Report</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {reviews?.length > 4 && (
        <div className="mt-6 text-center">
          <Button
            variant="outline"
            onClick={() => setShowAll(!showAll)}
            iconName={showAll ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            {showAll ? "Show Less" : `View All ${reviews?.length} Reviews`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmployeeReviews;